/**
 * The rules the public review page runs on.
 *
 * A person outside the company opens a URL, looks at four proofs, and presses
 * buttons that end in a document with their name on it. Everything below is
 * about not offering an action the database will refuse — because on this page
 * a refused action is a stranger being told "no" by software they did not
 * choose to use, with nobody to ask.
 *
 * The database enforces every one of these again. issue_signoff_certificate
 * counts active component types in the same transaction that writes the
 * certificate; order_assets_guard_locked refuses a write to a settled slot;
 * the edge function re-checks the note and the confirmation. This file is not
 * the enforcement. It is the part that keeps the page honest about what is
 * possible.
 */

import { LEGACY_ARTWORK_COMPONENTS } from '../artwork/componentVocabulary';

/** Mirrors public.asset_state. */
export type AssetState =
  'draft' | 'in_review' | 'approved' | 'rejected' | 'superseded' | 'final_approved';

export interface ReviewAsset {
  id: string;
  component_type: string;
  version: number;
  url: string;
  state: AssetState;
  /**
   * The client's own words on the most recent rejection, or ''.
   *
   * Optional because a payload from a deployed-but-older edge function will
   * not carry it, and a portal that throws on a missing field is a portal that
   * breaks for every client the moment the two halves are deployed a minute
   * apart.
   */
  latest_decision_note?: string;
  /**
   * The Drive identifier, when the edge function is new enough to send it.
   *
   * Optional for the same reason as the note above: the portal and the
   * function deploy separately, and a client hitting the page in the minute
   * between must still see something rather than a blank screen.
   */
  drive_file_id?: string;
}

export interface ReviewItem {
  id: string;
  product_type: string;
  description: string;
  units: number;
  unit_price_usd: number;
}

export interface ReviewOrder {
  order_no: string;
  client: string;
  shipping_handling_usd: number;
}

export interface ReviewCertificate {
  certificate_no: string;
  client_name: string;
  signed_at: string;
}

export interface ReviewPayload {
  order: ReviewOrder;
  items: ReviewItem[];
  assets: ReviewAsset[];
  certificate: ReviewCertificate | null;
  /**
   * The order's own vocabulary, when the edge function is new enough to send
   * it. Optional because frontend and function deploy separately. An older
   * endpoint falls back to the legacy four it has always served.
   */
  components?: string[];
}

export type SlotStatus =
  /** Nothing uploaded yet. */
  | 'awaiting'
  /** Live and waiting on the client. */
  | 'pending'
  /** The client approved it. Locked. */
  | 'approved'
  /** The client sent it back. Waiting on TAP. */
  | 'revision_requested'
  /** Frozen by the certificate. */
  | 'signed';

export interface ReviewSlot {
  component: string;
  asset: ReviewAsset | null;
  status: SlotStatus;
  /** Whether the client may act on it right now. */
  decidable: boolean;
  /**
   * The feedback to show in the callout, or ''.
   *
   * Carried on the slot rather than read off the asset at render time because
   * it is only meaningful for ONE status. An approved slot whose asset still
   * holds a note from an earlier round would otherwise show "we need the clasp
   * changed" underneath a green tick.
   */
  note: string;
}

const STATUS_OF: Record<AssetState, SlotStatus> = {
  // upload_order_asset writes 'draft' and nothing in the pipeline promotes it
  // to 'in_review' today. Treating only 'in_review' as live would show a
  // client four proofs and no way to respond to any of them.
  draft: 'pending',
  in_review: 'pending',
  approved: 'approved',
  rejected: 'revision_requested',
  final_approved: 'signed',
  // Never reaches the page: the edge function filters superseded rows out.
  // Mapped anyway so an unexpected one renders as history rather than as a
  // slot the client is invited to decide on.
  superseded: 'revision_requested',
};

/**
 * One slot per component, always four, whatever arrived.
 *
 * Four boxes is the promise the link made. A component with no asset yet is a
 * slot the client should see waiting, not a slot that is missing — and a
 * missing one is exactly the case that must NOT let a sign-off through.
 */
export function buildSlots(assets: ReviewAsset[], components: readonly string[]): ReviewSlot[] {
  return components.map((component) => {
    const asset = assets.find((entry) => entry.component_type === component) ?? null;
    if (asset === null) {
      return { component, asset: null, status: 'awaiting' as const, decidable: false, note: '' };
    }
    const status = STATUS_OF[asset.state];
    return {
      component,
      asset,
      status,
      decidable: status === 'pending',
      // Only while the slot is actually in revision. See ReviewSlot.note.
      note: status === 'revision_requested' ? (asset.latest_decision_note ?? '') : '',
    };
  });
}

/** Every component settled in the client's favour. */
export function allApproved(slots: ReviewSlot[]): boolean {
  return slots.every((slot) => slot.status === 'approved' || slot.status === 'signed');
}

/**
 * Whether to offer the sign-off form.
 *
 * Built from the SLOTS rather than from the asset list, so a component that
 * was never uploaded counts as outstanding. Counting approved rows instead
 * would let an order with three proofs sign off — the same gap
 * issue_signoff_certificate closes by comparing against active component
 * types rather than against what happens to exist.
 */
export function canSignOff(payload: ReviewPayload): boolean {
  if (payload.certificate !== null) {
    return false;
  }
  return allApproved(buildSlots(payload.assets, reviewComponents(payload)));
}

/** Backward-compatible while the edge function and frontend deploy separately. */
export function reviewComponents(payload: ReviewPayload): string[] {
  return Array.isArray(payload.components) && payload.components.length > 0
    ? payload.components
    : [...LEGACY_ARTWORK_COMPONENTS];
}

/** What is stopping this decision, or null. */
export function decisionProblem(
  decision: 'approved' | 'revision_requested',
  note: string,
): string | null {
  if (decision === 'revision_requested' && note.trim() === '') {
    return 'Tell us what needs to change before sending it back.';
  }
  return null;
}

export interface ReviewPricing {
  units: number;
  subtotalUsd: number;
  shippingUsd: number;
  totalUsd: number;
}

/** Coerce at the edge: PostgREST hands numeric back as a string often enough. */
function num(value: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Whole cents, so a rounding artefact never reaches a number somebody signs. */
function cents(value: number): number {
  return Math.round(num(value) * 100);
}

/**
 * What the client is agreeing to pay.
 *
 * Summed in cents rather than in dollars. 0.1 + 0.2 is the classic floating
 * point result, and on an invoice attached to a signature a stray cent is the
 * kind of thing that gets noticed and not the kind that gets forgiven.
 */
export function pricing(items: ReviewItem[], shippingHandlingUsd: number): ReviewPricing {
  let units = 0;
  let subtotal = 0;
  for (const item of items) {
    const quantity = Math.round(num(item.units));
    units += quantity;
    subtotal += cents(item.unit_price_usd) * quantity;
  }
  const shipping = cents(shippingHandlingUsd);
  return {
    units,
    subtotalUsd: subtotal / 100,
    shippingUsd: shipping / 100,
    totalUsd: (subtotal + shipping) / 100,
  };
}

export function formatUsd(value: number): string {
  return `$${num(value).toFixed(2)}`;
}

// ── Proof previews ─────────────────────────────────────────────────────────

/**
 * Hosts a proof image may be loaded from.
 *
 * The same set the comment sanitiser uses, and for a stronger reason here:
 * this page is public and unauthenticated. An <img src> pointing anywhere
 * reachable would let whoever set the URL learn the IP, rough location and
 * user agent of every client who opens their review link — a tracking pixel
 * with extra steps. If the host is not one of these, the page falls back to
 * the link it has always shown.
 */
const ALLOWED_PROOF_HOSTS = new Set([
  'drive.google.com',
  'lh3.googleusercontent.com',
  'drive.usercontent.google.com',
]);

function allowedHost(raw: string): boolean {
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:' && ALLOWED_PROOF_HOSTS.has(parsed.hostname);
  } catch {
    return false;
  }
}

/** The Drive file id inside a share URL, or null. */
export function driveFileIdFrom(url: string): string | null {
  const byPath = /\/file\/d\/([A-Za-z0-9_-]+)/.exec(url);
  if (byPath?.[1] !== undefined) {
    return byPath[1];
  }
  const byQuery = /[?&]id=([A-Za-z0-9_-]+)/.exec(url);
  return byQuery?.[1] ?? null;
}

/**
 * What to put in the <img> for a proof, or null to show only the link.
 *
 * A Drive share URL is a VIEWER PAGE, not an image — dropping it straight into
 * an <img> renders a broken icon, which is worse than the text link it
 * replaced. Drive's thumbnail endpoint takes the file id and returns the
 * actual bytes, so the id is what matters and the URL is only one way to find
 * it.
 */
export function proofImageSrc(asset: ReviewAsset): string | null {
  return proofThumbnail(
    asset.url,
    asset.drive_file_id !== undefined && asset.drive_file_id !== '' ? asset.drive_file_id : null,
  );
}

/**
 * The same conversion, for callers that hold a URL rather than a whole asset.
 *
 * The certificate, the job order panel and the frozen PO all show the approved
 * artwork, and all three receive it as `{ component, version, url }` from a
 * database function — no asset row, no drive_file_id column. Each of them put
 * that URL straight into an <img> and each of them rendered a row of broken
 * icons on the factory's screen, which is the failure this file already
 * described in the paragraph above and then only fixed in one place.
 *
 * The id is derived from the URL when it is not supplied. A Drive webViewLink
 * carries it in the path, and a direct googleusercontent link needs no id at
 * all — so both shapes work without widening any payload.
 */
export function proofThumbnail(url: string, driveFileId: string | null = null): string | null {
  const asset = { url, drive_file_id: driveFileId ?? '' };
  if (!allowedHost(asset.url)) {
    return null;
  }
  const fileId =
    asset.drive_file_id !== undefined && asset.drive_file_id !== ''
      ? asset.drive_file_id
      : driveFileIdFrom(asset.url);
  if (fileId !== null) {
    // w1600 is large enough to judge artwork on a laptop and small enough not
    // to make a phone on hotel wifi wait for a print-resolution file.
    return `https://drive.google.com/thumbnail?id=${encodeURIComponent(fileId)}&sz=w1600`;
  }
  // Already a direct image on an allowed host — googleusercontent links are.
  return asset.url;
}

/**
 * One sentence, under the title, saying what is being asked of the reader.
 *
 * ── Why a page needs this ──────────────────────────────────────────────────
 *
 * Measured on a phone at 390px — which is where a client actually opens an
 * emailed link — this page is over 2,000 pixels tall. Three proofs, one of
 * them waiting, and the only way to find that out is to scroll past all of
 * them. Everyone who opens this has no account, no colleague to ask, and no
 * idea whether they are supposed to do something today.
 *
 * So the answer goes at the top, in words, before anything else. It is the
 * cheapest thing on this page and probably the most useful.
 *
 * The order of the checks is the order that matters to the reader, not the
 * order the states are declared in: a signed order is finished whatever else
 * is true, and something waiting on THEM outranks something waiting on us.
 */
export function whatIsWaiting(slots: ReviewSlot[], signedOff: boolean): string {
  if (signedOff) {
    return 'Signed off. Nothing else is needed from you.';
  }

  const total = slots.length;
  const waiting = slots.filter((slot) => slot.status === 'pending').length;

  if (waiting > 0) {
    // "1 of 3 proofs is" / "2 of 3 proofs are". Reading wrong makes a careful
    // page look careless, and this sentence is the first one anybody reads.
    const verb = waiting === 1 ? 'is' : 'are';
    return `${String(waiting)} of ${String(total)} proofs ${verb} waiting on you.`;
  }

  if (total > 0 && slots.every((slot) => slot.status === 'approved')) {
    return 'Every proof is approved. Sign off at the bottom and we go to production.';
  }

  if (slots.some((slot) => slot.status === 'revision_requested')) {
    // Deliberately says who has it. "Nothing is waiting on you" alone reads as
    // "nothing is happening", which is when a client emails to ask.
    return 'We are working on the changes you asked for. Nothing is waiting on you right now.';
  }

  return 'Nothing to review yet. We will email you as soon as the first proof is ready.';
}
